import {isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DcpHttpUrlEncodingService} from '@app/services/dcp-url-parameter-codec.service';
import {FacetSearchService} from '@app/services/facet-search.service';
import {ConfigService, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Facet, FacetValue, ProductSearchPage, ProductSearchService, RoutingService} from '@spartacus/core';
import {LAUNCH_CALLER, LaunchDialogService, SearchCriteria} from '@spartacus/storefront';
import {throttle} from 'lodash-es';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {CustomProductListComponentService} from '../../services/custom-product-list-component.service';

export const DESKTOP_VISIBLE_FACETS: number = 9;
export const SEARCH_ENABLED_THRESHOLD: number = 10;

type SearchTabNav = {
  id: string;
  text: string;
};
@UntilDestroy()
@Component({
  selector: 'dcp-search-navigation-bar',
  templateUrl: './search-navigation-bar.component.html',
  styleUrls: ['./search-navigation-bar.component.scss']
})
export class SearchNavigationBarComponent implements OnInit, OnDestroy {

  searchSubscription: Subscription = Subscription.EMPTY;

  searchResult$: Observable<ProductSearchPage>;
  visibleFacets$: Observable<Facet[]>;
  model$: Observable<ProductSearchPage>;
  /** An object that stores facet's scroll position and loading status */
  facetScrollPosition: { position: number; isLoading: boolean };
  changeStickyNav = true;
  mergedFacets$: Observable<Facet[]>;
  facetsForDesktop: number = DESKTOP_VISIBLE_FACETS;
  element: HTMLElement;
  footer: HTMLElement;
  tabNavItems: SearchTabNav[] = [
    {id: 'defaultSourceGroup', text: 'All results'},
    {id: 'Products', text: 'Products'},
    {id: 'Community', text: 'Community'},
    {id: 'Support', text: 'Support'},
  ];

  @ViewChild('mobileFacets', {read: ViewContainerRef}) vcr;
  @ViewChild('elementRef', {read: ElementRef}) elementRef;

  protected queryCodec: DcpHttpUrlEncodingService;
  private scrollCallback: () => void;
  protected query$: Observable<string> = this.routingService.getRouterState().pipe(map((state) => state.state.params.query));
  private querySubscription: Subscription = Subscription.EMPTY;

  constructor(
    @Inject(PLATFORM_ID) public platformId: string,
    public facetSearchService: FacetSearchService,
    public launchDialogService: LaunchDialogService,
    protected productSearchService: ProductSearchService,
    protected productListComponentService: CustomProductListComponentService,
    protected cd: ChangeDetectorRef,
    protected jsonService: JsonService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected routingService: RoutingService,
    protected configService: ConfigService,
  ) {
    this.queryCodec = new DcpHttpUrlEncodingService();
  }

  ngOnInit() {
    this.model$ = this.productListComponentService.model$;
    if (isPlatformBrowser(this.platformId)) {
      this.element = document.querySelector('dcp-search-navigation-bar');
      this.footer = document.querySelector('footer');
    }

    this.searchResult$ = this.productListComponentService.model$;
    this.visibleFacets$ = this.searchResult$.pipe(
      map(searchResult => searchResult.facets
        ? searchResult.facets.filter(facet => facet.visible)
        : [])
    );

    this.mergedFacets$ = combineLatest([this.visibleFacets$, this.facetSearchService.backendFacets]).pipe(
      map(([visibleFacets, allFacets]) => this.mergeSearchFacets(visibleFacets, allFacets))
    );
  }

  getBottomOffsetCallback() {
    return throttle(() => {
      if (isPlatformBrowser(this.platformId) && this.element && this.footer) {
        const scrollPosition: number = window.scrollY;
        const windowSize: number = window.innerHeight;
        const footerOffset: number = this.footer.offsetTop;
        this.element.style.bottom = Math.max(scrollPosition + windowSize - footerOffset, 0) + 'px';
      }
    }, 10);
  }

  /**
   * Method merges facets from search result page into facets
   * from backend.
   *
   * @param searchPageFacets facets from search result page
   * @param dcpFacets facets from backend
   */
  mergeSearchFacets(searchPageFacets: Facet[], dcpFacets: Facet[]): Facet[] {
    const result: Facet[] = this.jsonService.parseSafely(JSON.stringify(dcpFacets));

    searchPageFacets.forEach(searchFacet => {
      const changedFacet: Facet = result.find((facet) => facet.name === searchFacet.name);
      if (changedFacet) {
        changedFacet.values.forEach((facetValue, i) => {
          const changedFacetValue: FacetValue = searchFacet.values.find(sfv => sfv.name === facetValue.name);
          if (changedFacetValue) {
            changedFacet.values[i] = changedFacetValue;
          }
        });
      }
    });
    return result;
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.changeStickyNav) {
      window.removeEventListener('scroll', this.scrollCallback);
      window.removeEventListener('resize', this.scrollCallback);
    }
    this.searchSubscription.unsubscribe();
    this.querySubscription.unsubscribe();
  }

  toggleFacetValueList(facet: Facet, facetValueNames: string[]) {
    let query = '';
    const toggledFacetValue: FacetValue = this.getFacetValueByName(facet, facetValueNames[0]);
      if (facetValueNames.length > 1) {
        const splittedQuery = this.getQuery(toggledFacetValue)?.split(':');
        query = splittedQuery.filter((item, index, array) =>
          !(facetValueNames.indexOf(item) !== -1 || facetValueNames.indexOf(array[index + 1])!== -1)).join(':');
      } else {
        query = this.getQuery(toggledFacetValue);
      }
      this.route({query, currentPage: undefined});
  }

  protected route(queryParams: SearchCriteria): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }

  getFacetValueByName(facet: Facet, facetValueNames: string): FacetValue {
    return facet.values.find(facetValue => facetValue.name === facetValueNames);
  }

  openNavBar() {
    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.SEARCH_NAVBAR, null, this.mergedFacets$);
  }

  /**
   * Sorts products on search result page
   * by given sort code
   */
  sortList(sortCode: any): void {
    this.productListComponentService.sort(sortCode);
  }

  onTabClick(e: CustomEvent): void {
    if (e.detail.currentPage !== 2) {
      this.querySubscription = this.query$.pipe(take(1), untilDestroyed(this)).subscribe((value) => {
        const term: string = value ? `t=${value}&` : '';
        const baseUrl: string = this.configService.getString('sapcom.search');
        if (isPlatformBrowser(this.platformId) && this.tabNavItems[e.detail.currentPage - 1]) {
          window.location.href = `${baseUrl}?${term}n=1&s=_score&src=${this.tabNavItems[e.detail.currentPage - 1].id}`;
        }
      });
    }
  }

  private getQuery(facetValue: FacetValue): string {
    return this.queryCodec.decodeValue(facetValue.query.query.value).replace(/\+/g, ' ');
  }
}
